import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import { Heading, Subheading } from "../../design/heading";
import { Text } from "../../design/text";
import { Badge } from "../../design/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../design/table";
import { Visit, VisitStatus, Supervisor } from "@visitproof/types";
import {
  collection,
  query,
  where,
  onSnapshot,
  doc,
  orderBy,
} from "firebase/firestore";
import { db } from "../../firebase";
import moment from "moment";
import {
  CalendarIcon,
  ClockIcon,
  MapPinIcon,
} from "@heroicons/react/20/solid";

const getStatusBadge = (status: VisitStatus) => {
  switch (status) {
    case "scheduled":
      return <Badge color="blue">Scheduled</Badge>;
    case "supervisor_enroute":
      return <Badge color="yellow">En Route</Badge>;
    case "supervisor_arrived":
      return <Badge color="orange">Arrived</Badge>;
    case "supervisor_started":
      return <Badge color="green">In Progress</Badge>;
    case "supervisor_ended":
    case "completed":
      return <Badge color="zinc">Completed</Badge>;
    case "canceled":
      return <Badge color="red">Canceled</Badge>;
    case "no_show":
      return <Badge color="red">No Show</Badge>;
    default:
      return <Badge color="zinc">{status}</Badge>;
  }
};

const VisitCard: React.FC<{ visit: Visit }> = ({ visit }) => {
  const startTime = moment(visit.startDate.toDate());
  const endTime = moment(visit.endDate.toDate());

  return (
    <Link
      to={`/visit/${visit.id}`}
      className="block p-4 transition-colors border rounded-lg border-zinc-200 dark:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-800"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <CalendarIcon className="w-4 h-4 text-zinc-500" />
            <Text className="font-medium">
              {startTime.format("dddd, MMMM D, YYYY")}
            </Text>
          </div>
          <div className="flex items-center gap-2 mb-2">
            <ClockIcon className="w-4 h-4 text-zinc-500" />
            <Text>
              {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
            </Text>
          </div>
          {visit.location && (
            <div className="flex items-center gap-2">
              <MapPinIcon className="w-4 h-4 text-zinc-500" />
              <Text className="text-zinc-600 dark:text-zinc-400">
                {visit.location.name || visit.location.formattedAddress}
              </Text>
            </div>
          )}
        </div>
        <div>{getStatusBadge(visit.status)}</div>
      </div>
    </Link>
  );
};

const VisitTable: React.FC<{ visits: Visit[]; title: string }> = ({
  visits,
  title,
}) => {
  if (visits.length === 0) {
    return null;
  }

  return (
    <div className="mt-8">
      <Subheading className="mb-4">{title}</Subheading>
      <Table>
        <TableHead>
          <TableRow>
            <TableHeader>Date</TableHeader>
            <TableHeader>Time</TableHeader>
            <TableHeader>Location</TableHeader>
            <TableHeader>Status</TableHeader>
          </TableRow>
        </TableHead>
        <TableBody>
          {visits.map((visit) => {
            const startTime = moment(visit.startDate.toDate());
            const endTime = moment(visit.endDate.toDate());
            return (
              <TableRow key={visit.id} href={`/visit/${visit.id}`}>
                <TableCell>{startTime.format("MMM D, YYYY")}</TableCell>
                <TableCell>
                  {startTime.format("h:mm A")} - {endTime.format("h:mm A")}
                </TableCell>
                <TableCell>
                  {visit.location?.name || visit.location?.formattedAddress || "—"}
                </TableCell>
                <TableCell>{getStatusBadge(visit.status)}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

const SupervisorDashboard: React.FC = () => {
  const { user, claims, loading: authLoading } = useAuth();
  const [visits, setVisits] = useState<Visit[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const accountId = claims?.accountId || null;
  const supervisorId = accountId ? claims?.accounts?.[accountId]?.supervisorId || null : null;

  // Load visits
  useEffect(() => {
    if (authLoading || !supervisorId) {
      setVisits([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    const visitsQuery = query(
      collection(db, "visits"),
      where("supervisorId", "==", supervisorId),
      orderBy("startDate", "asc")
    );

    const unsubscribe = onSnapshot(
      visitsQuery,
      (snapshot) => {
        const visitList = snapshot.docs.map((d) => ({
          id: d.id,
          ...d.data(),
        })) as Visit[];
        setVisits(visitList);
        setLoading(false);
      },
      (err) => {
        console.error("Error loading visits:", err);
        setError(err.message);
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, [authLoading, supervisorId]);

  // Computed visit lists
  const { todayVisits, upcomingVisits } = useMemo(() => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59);

    const today = visits.filter((visit) => {
      const visitDate = visit.startDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate >= startOfToday && visitDate <= endOfToday;
    });

    const upcoming = visits.filter((visit) => {
      const visitDate = visit.startDate?.toDate?.();
      if (!visitDate) return false;
      return visitDate > endOfToday && visit.status !== "canceled" && visit.status !== "completed";
    });

    return { todayVisits: today, upcomingVisits: upcoming };
  }, [visits]);

  if (loading || authLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Text>Loading...</Text>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 rounded-lg bg-red-50 dark:bg-red-900/20">
        <Text className="text-red-600 dark:text-red-400">{error}</Text>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-8">
        <Heading>
          Welcome back{user ? `, ${user.displayName}` : ""}
        </Heading>
        <Text className="mt-2 text-zinc-600 dark:text-zinc-400">
          Here's an overview of your scheduled visits
        </Text>
      </div>

      {/* Today's Visits */}
      <div className="mb-8">
        <Subheading className="mb-4">Today's Visits</Subheading>
        {todayVisits.length === 0 ? (
          <div className="p-6 text-center border rounded-lg border-zinc-200 dark:border-zinc-700">
            <Text className="text-zinc-500">No visits scheduled for today</Text>
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {todayVisits.map((visit) => (
              <VisitCard key={visit.id} visit={visit} />
            ))}
          </div>
        )}
      </div>

      {/* Upcoming Visits Table */}
      <VisitTable visits={upcomingVisits.slice(0, 10)} title="Upcoming Visits" />

      {upcomingVisits.length > 10 && (
        <div className="mt-4">
          <Link
            to="/visits"
            className="text-sm font-medium text-blue-600 hover:text-blue-500"
          >
            View all {upcomingVisits.length} upcoming visits →
          </Link>
        </div>
      )}
    </div>
  );
};

export default SupervisorDashboard;
